import { TraverseMarkers } from '../TraverseMarkers.js';
import calibratePhoto from '../../elements/calibrate-photo/calibrate-photo.js';
import { CalcCameraOrientation } from '../CalcCameraOrientation.js';
import { Convert } from '../Convert.js';
import { IsPhotoDisplayedLandscape } from '../IsPhotoDisplayedLandscape.js';

/**
 * @typedef {object} InfoSeverity
 * @property {'Info'} name - The name of the severity.
 * @property {'var(--paper-blue-500)'} iconColor - The icon color for the severity.
 * @property {'info'} icon - The icon for the severity.
 */

/**
 * @typedef {object} WarningSeverity
 * @property {'Warning'} name - The name of the severity.
 * @property {'var(--paper-orange-500)'} iconColor - The icon color for the severity.
 * @property {'warning'} icon - The icon for the severity.
 */

/**
 * @typedef {object} ErrorSeverity
 * @property {'Error'} name - The name of the severity.
 * @property {'var(--paper-red-500)'} iconColor - The icon color for the severity.
 * @property {'error'} icon - The icon for the severity.
 */

/**
 * The severity types for warnings.
 * @type {{INFO: InfoSeverity, WARNING: WarningSeverity, ERROR: ErrorSeverity}}
 */
const WARNING_SEVERITY_TYPES = {
  INFO: {
    name: 'Info',
    iconColor: 'var(--paper-blue-500)',
    icon: 'info'
  },
  WARNING: {
    name: 'Warning',
    iconColor: 'var(--paper-orange-500)',
    icon: 'warning'
  },
  ERROR: {
    name: 'Error',
    iconColor: 'var(--paper-red-500)',
    icon: 'error'
  }
};

/**
 * The severity of a warning from the WARNING_SEVERITY enum
 * @typedef {(InfoSeverity|WarningSeverity|ErrorSeverity)} PhotoWarningSeverity
 */

/**
 * @typedef {object} PhotoWarning
 * @property {PhotoWarningSeverity} severity - The severity of the warning
 * @property {string} category - The category (or name) of the warning.
 * @property {string} description - The description of the warning.
 * @property {string} type - The type of the warning (either `'photo'` or `'marker'`).
 * @property {string} [markerPath] - For marker warnings, the dot-delimited path to the marker.
 */

/**
 * Returns a sorted array of photo warnings, sorted by severity.
 * @param {PhotoWarning[]} warnings - The warnings to sort.
 * @returns {PhotoWarning[]} The sorted warnings.
 */
export function sortPhotoWarnings(warnings) {
  const sortOrder = [WARNING_SEVERITY_TYPES.ERROR.name, WARNING_SEVERITY_TYPES.WARNING.name, WARNING_SEVERITY_TYPES.INFO.name];
  return warnings.sort((a, b) => {
    const aIndex = sortOrder.indexOf(a.severity.name);
    const bIndex = sortOrder.indexOf(b.severity.name);
    return aIndex - bIndex;
  });
}

/**
 * Gets the warnings for a photo, including warnings for its markers.
 * @param {object} photoData - The photo data (from `photos.<photo-id>` on the job).
 * @param {object} [supplementalData] - Supplemental data for generating the warnings.
 * @returns {PhotoWarning[]} An array of warnings for the photo.
 */
export function getWarningsForPhoto(photoData, supplementalData) {
  const { photofirst_data: photofirstData } = photoData;
  if (!photofirstData) return [];

  // Remove _editors from photofirstData
  const { _editors, ...photofirstDataWithoutEditors } = photofirstData;
  const warningsForAllMarkers = /** @type {PhotoWarning[]} */ ([]);
  let unfilteredWarningsOnPhoto = [];
  if (photoData.cloneable_photo || photoData.azmyth_photo) {
    TraverseMarkers(photofirstDataWithoutEditors, (marker, markerPath) => {
      warningsForAllMarkers.push(
        ...getWarningsForMarkerExcludingDistortionCorrection(marker, { markerPath, photoData, ...supplementalData })
      );
    });
    unfilteredWarningsOnPhoto = [
      checkIsCloneablePhoto(photoData),
      checkIsAzmythPhoto(photoData),
      ...checkPoleTopMarkers(photofirstDataWithoutEditors)
    ];
  } else {
    TraverseMarkers(photofirstDataWithoutEditors, (marker, markerPath) => {
      warningsForAllMarkers.push(...getWarningsForMarker(marker, { markerPath, photoData, ...supplementalData }));
    });
    const orientationInputs = getOrientationInputs(photoData, photofirstDataWithoutEditors);
    const cameraOrientation = getCameraOrientation(orientationInputs, photoData);
    unfilteredWarningsOnPhoto = [
      checkAnglesOfAttack(cameraOrientation),
      checkDistance(cameraOrientation, orientationInputs?.highestMarker?.height),
      checkPhotoIsLandscape(photoData),
      checkPhotoFocalLength(photoData),
      checkIsCloneablePhoto(photoData),
      checkIsAzmythPhoto(photoData),
      ...checkPoleTopMarkers(photofirstDataWithoutEditors),
      checkAnchorResiduals(photoData)
    ];
  }

  // Filter out null warnings
  const warningsOnPhoto = unfilteredWarningsOnPhoto.filter((warning) => warning !== null);

  // Combine the warnings lists
  return [...warningsOnPhoto, ...warningsForAllMarkers];
}

/**
 * Gets the warnings for a marker.
 * @param {object} markerData - The marker data.
 * @param {object} supplementalData - Supplemental data for generating the warnings.
 * @param {string} supplementalData.markerPath - The dot-delimited path to the marker (from `photofirst_data`).
 * @param {object} supplementalData.photoData - The photo data (from `photos.<photo-id>` on the job).
 * @param {object} supplementalData.photoElementModels - The photo element models for the job.
 * @returns {PhotoWarning[]} An array of warnings for the marker.
 */
export function getWarningsForMarker(markerData, supplementalData) {
  const warningsOnMarker = [
    checkMarkerIsOffPhoto(markerData, supplementalData),
    getMarkerIsTooCloseToEdge(markerData, supplementalData),
    checkMarkerPhotoElementModelExists(supplementalData),
    getMarkerIsTooFarFromStickAlign(markerData, supplementalData),
    checkMarkerAbovePoleTop(markerData, supplementalData),
    checkAnchorAlignmentScore(markerData, supplementalData)
  ];

  // Filter out null warnings
  return warningsOnMarker.filter((warning) => warning !== null);
}

/**
 * Gets the warnings for a marker not including warnings related to distortion correction.
 * @param {object} markerData - The marker data.
 * @param {object} supplementalData - Supplemental data for generating the warnings.
 * @param {string} supplementalData.markerPath - The dot-delimited path to the marker (from `photofirst_data`).
 * @param {object} supplementalData.photoData - The photo data (from `photos.<photo-id>` on the job).
 * @param {object} supplementalData.photoElementModels - The photo element models for the job.
 * @returns {PhotoWarning[]} An array of warnings for the marker.
 */
export function getWarningsForMarkerExcludingDistortionCorrection(markerData, supplementalData) {
  const warningsOnMarker = [
    checkMarkerIsOffPhoto(markerData, supplementalData),
    checkMarkerPhotoElementModelExists(supplementalData),
    checkMarkerAbovePoleTop(markerData, supplementalData)
  ];

  // Filter out null warnings
  return warningsOnMarker.filter((warning) => warning !== null);
}

/**
 * Checks if a photo's focal length is less than 28mm.
 * @param {object} photoData - The photo data (from `photos.<photo-id>` on the job).
 * @returns {PhotoWarning?}
 */
function checkPhotoFocalLength(photoData) {
  // If focal length is missing, return an error
  const focalLength = photoData.focal_length;
  if (focalLength === undefined)
    return {
      severity: WARNING_SEVERITY_TYPES.ERROR,
      category: 'focal_length_missing',
      description: 'Focal length is missing.',
      type: 'photo'
    };

  // If focal length is less than 28mm, return a warning
  const focalLengthTooLow = focalLength < 28;
  return focalLengthTooLow
    ? {
        severity: WARNING_SEVERITY_TYPES.INFO,
        category: 'low_focal_length',
        description: `This photo was taken with a focal length lower than 28mm. Low focal lengths can magnify the effects of errors in stick placement, calibration, leaning or warped poles, parallax, and lens distortion.`,
        type: 'photo'
      }
    : null;
}

/**
 * Determines if a photo is in landscape orientation and returns a warning object if it is.
 *
 * @param {Object} photoData - The photo data object.
 * @returns {PhotoWarning?} A warning object if the photo is in landscape orientation, otherwise null.
 */
function checkPhotoIsLandscape(photoData) {
  const { image_width: width, image_height: height } = photoData;
  const isLandscape = width > height;

  const isLandscapeWarning = {
    severity: WARNING_SEVERITY_TYPES.WARNING,
    category: 'photo_is_landscape',
    description:
      'We recommend that height photos are taken in portrait orientation. Photos in landscape orientation have fewer usable pixels for both calibration and cable identification.',
    type: 'photo'
  };

  return isLandscape ? isLandscapeWarning : null;
}

/**
 * Checks if a marker is off the photo.
 * @param {object} markerData - The marker data.
 * @param {object} supplementalData - Supplemental data for generating the warnings.
 * @param {string} supplementalData.markerPath - The dot-delimited path to the marker (from `photofirst_data`).
 * @returns {PhotoWarning?}
 */
function checkMarkerIsOffPhoto(markerData, supplementalData) {
  const { markerPath } = supplementalData;
  const { pixel_selection: pixelSelection } = markerData;

  if (!pixelSelection || pixelSelection.length === 0) return null;

  // Get the first pixel selection's X and Y percentages
  const markerIsOffPhoto = markerIsOffPhotoHelper(pixelSelection[0]);

  return markerIsOffPhoto
    ? {
        severity: WARNING_SEVERITY_TYPES.ERROR,
        category: 'marker_off_photo',
        description: 'Marker is off the photo.',
        type: 'marker',
        markerPath
      }
    : null;
}

/**
 * Checks if a marker's photo element model exists.
 * @param {object} supplementalData - Supplemental data for generating the warnings.
 * @param {string} supplementalData.markerPath - The dot-delimited path to the marker (from `photofirst_data`).
 * @param {object} supplementalData.photoElementModels - The photo element models for the job.
 * @returns {PhotoWarning?}
 */
function checkMarkerPhotoElementModelExists(supplementalData) {
  const { markerPath, photoElementModels } = supplementalData;

  // Get the marker's photo element from the path (the marker id is the last element, the photo element is the second-to-last)
  const markerPhotoElement = markerPath.split('.').at(-2);

  // Check if the photo element exists
  const photoElementExists = markerPhotoElement !== undefined && photoElementModels?.[markerPhotoElement] !== undefined;

  return !photoElementExists
    ? {
        severity: WARNING_SEVERITY_TYPES.ERROR,
        category: 'invalid_photo_element',
        description: `Missing photo element data for "${markerPhotoElement}". Please re-import this photo element in the Model Editor.`,
        type: 'marker',
        markerPath
      }
    : null;
}

/**
 * @typedef {object} PixelSelection
 * @property {number} percentX - The percentage of the x-coordinate of the pixel selection.
 * @property {number} percentY - The percentage of the y-coordinate of the pixel selection.
 */

/**
 * @typedef {object} CorrectedPixel
 * @property {number} correctedX - The corrected x-coordinate of the pixel.
 * @property {number} correctedY - The corrected y-coordinate of the pixel.
 */
/**
 * Corrects the pixel selection data to the distortion corrected pixel coordinates
 * @param {PixelSelection} pixelSelection Pixel selection data in percentage
 * @param {number} pixelWidth Image width in pixels
 * @param {number} pixelHeight Image height in pixels
 * @param {Object} stickAlign Stick alignment data
 * @returns {CorrectedPixel} Corrected pixel coordinates
 */
function _pixelSelection2CorrectedPixel(pixelSelection, pixelWidth, pixelHeight, stickAlign) {
  const x = (pixelSelection.percentX / 100) * pixelWidth;
  const y = (pixelSelection.percentY / 100) * pixelHeight;
  const correctedPixel = calibratePhoto.correctPixel(x, y, stickAlign);

  return correctedPixel;
}

/**
 * @typedef {object} CorrectedMarker
 * @property {number} x - The corrected x pixel coordinate of the marker.
 * @property {number} y - The corrected y pixel coordinate of the marker.
 * @property {number} height - The real-world height of the marker.
 * @property {string} [type] - The type of marker.
 */
/**
 * Gets the distortion corrected highest marker pixel coordinates and corresponding real-world height
 * @param {Object} photoFirstData
 * @param {number} pixelWidth Image width in pixels
 * @param {number} pixelHeight Image height in pixels
 * @param {Object} stickAlign Stick alignment data
 * @returns {CorrectedMarker?} The corrected highest marker pixel coordinates and real-world height, or null if no highest marker is found.
 */
function _getCorrectedHighestMarker(photoFirstData, pixelWidth, pixelHeight, stickAlign) {
  let highestMarker = null;
  TraverseMarkers(
    photoFirstData,
    (marker) => {
      let height = null;
      if (marker?.height) {
        height = Convert(marker.height, 'ft', 'mm');
      } else if (marker?._measured_height) {
        height = Convert(marker._measured_height, 'in', 'mm');
      }
      if (typeof height !== 'number') return;
      if (!marker?.pixel_selection || marker?.pixel_selection.length === 0) return;
      if (highestMarker === null || height > highestMarker.height) {
        const { correctedX, correctedY } = _pixelSelection2CorrectedPixel(marker.pixel_selection[0], pixelWidth, pixelHeight, stickAlign);
        highestMarker = {
          height,
          x: correctedX,
          y: correctedY,
          type: 'highest marker'
        };
      }
    },
    [],
    null,
    ['anchor_calibration']
  );
  return highestMarker;
}

/**
 * Gets the distortion corrected calibration anchor pixel coordinates
 * @param {Object} photoFirstData
 * @param {number} pixelWidth Image width in pixels
 * @param {number} pixelHeight Image height in pixels
 * @param {Object} stickAlign Stick alignment data
 * @returns {CorrectedMarker[]?} The corrected calibration anchor pixel coordinates, or null if not enough anchors are found.
 */
function _getCorrectedAnchors(photoFirstData, pixelWidth, pixelHeight, stickAlign) {
  const photoAnchors = Object.values(photoFirstData.anchor_calibration || {});
  if (photoAnchors.length < 3) return null; // We need at least 3 anchors to calculate the camera orientation
  let correctedAnchors = [];
  for (const anchor of photoAnchors) {
    const pixelSelection = anchor.pixel_selection[0];
    const { correctedX, correctedY } = _pixelSelection2CorrectedPixel(pixelSelection, pixelWidth, pixelHeight, stickAlign);
    const anchorHeight = Convert(anchor.height, 'ft', 'mm');
    if (typeof correctedX !== 'number' || typeof correctedY !== 'number' || typeof anchorHeight !== 'number') continue;
    correctedAnchors.push({
      x: correctedX,
      y: correctedY,
      height: anchorHeight,
      type: 'stick anchor'
    });
  }
  if (correctedAnchors.length < 3) return null;

  return correctedAnchors;
}

/**
 * Get the distortion corrected pixel coordinates of the stick bottom
 * @param {Object} stickAlign Stick alignment data
 * @returns {CorrectedMarker} The corrected stick bottom pixel coordinates
 */
function _getCorrectedStickBottom(stickAlign) {
  const stickBottom = calibratePhoto.getPixel(0, stickAlign);
  const correctedStickBottom = calibratePhoto.correctPixel(stickBottom.x, stickBottom.y, stickAlign);
  return {
    x: correctedStickBottom.correctedX,
    y: correctedStickBottom.correctedY,
    height: 0,
    type: 'stick bottom'
  };
}

/**
 * Gather the inputs needed to calculate the camera orientation
 * @param {Object} photoData All photo data
 * @param {Object} photoFirstData Photofirst data
 * @returns {Object?} The inputs needed to calculate the camera orientation, or null if the input data is invalid.
 */
function getOrientationInputs(photoData, photoFirstData) {
  const stickAlign = photoData?.stick_align;
  const cameraModel = calibratePhoto.getMatchingCameraModel(photoData);
  const pixelWidth = photoData?.image_width;
  const pixelHeight = photoData?.image_height;
  const focalLength = photoData?.focal_length;
  const sensorWidth = cameraModel?.sensorWidth;
  const sensorHeight = cameraModel?.sensorHeight;
  if (!stickAlign) return null;
  const correctedAnchors = _getCorrectedAnchors(photoFirstData, pixelWidth, pixelHeight, stickAlign);
  const stickBottomMarker = _getCorrectedStickBottom(stickAlign);
  const highestMarker = _getCorrectedHighestMarker(photoFirstData, pixelWidth, pixelHeight, stickAlign);
  if (
    typeof focalLength !== 'number' ||
    typeof sensorHeight !== 'number' ||
    typeof sensorWidth !== 'number' ||
    typeof pixelHeight !== 'number' ||
    typeof pixelWidth !== 'number' ||
    correctedAnchors === null ||
    correctedAnchors.length < 3 ||
    !highestMarker
  )
    return null;

  return {
    correctedAnchors,
    stickBottomMarker,
    highestMarker,
    focalLength: photoData?.focal_length,
    sensorWidth: cameraModel?.sensorWidth,
    sensorHeight: cameraModel?.sensorHeight,
    pixelWidth,
    pixelHeight
  };
}

/**
 * @typedef {object} CameraOrientation
 * @property {number} bestAngle - The best angle of the stick relative to the camera.
 * @property {number} lowerAngleOfAttack - The lower angle of attack.
 * @property {number} upperAngleOfAttack - The upper angle of attack.
 * @property {number} distToStickBottom - The distance to the bottom of the stick.
 * @property {number} relativeHeightOfCamera - The relative height of the camera to the lowest marker.
 */

/**
 * Calculates the camera orientation relative to a stick in a calibrated photo
 * @param {Object?} inputs The inputs needed to calculate the camera orientation, or null if the input data is invalid.
 * @param {Object} photoData All photo data
 * @returns {CameraOrientation?} The camera orientation data, or null if the input data is invalid.
 */
function getCameraOrientation(inputs, photoData) {
  if (!inputs) return null;
  // Get the sensor and focal length data
  const stickBottomMarker = inputs?.stickBottomMarker || {};
  const highestMarker = inputs?.highestMarker || {};
  let { correctedAnchors, focalLength, sensorHeight, sensorWidth, pixelHeight, pixelWidth } = inputs;

  const isLandscape = IsPhotoDisplayedLandscape(photoData);
  const katapultRotation = photoData?.rotation || 0;
  if (
    typeof focalLength !== 'number' ||
    typeof sensorHeight !== 'number' ||
    typeof sensorWidth !== 'number' ||
    typeof pixelHeight !== 'number' ||
    typeof pixelWidth !== 'number' ||
    photoData?.stick_align === null ||
    isLandscape == null
  )
    return null;
  const stickAlign = photoData.stick_align;
  // If the photo is in portrait orientation, swap the sensor dimensions stored in camera models
  if (!isLandscape) {
    [sensorHeight, sensorWidth] = [sensorWidth, sensorHeight];
  }
  // If the photo is dispalyed as rotated 90 or 270 degrees,
  // swap the pixel dimensions provided in the exif data
  if (katapultRotation === 1 || katapultRotation === 3) {
    [pixelHeight, pixelWidth] = [pixelWidth, pixelHeight];
  }

  let inputPoints = [...correctedAnchors, stickBottomMarker, highestMarker];
  const filteredInputPoints = inputPoints.filter((point) => point !== null);
  filteredInputPoints.sort((a, b) => a.height - b.height);

  // TODO: Make sure orientation is correct i.e. sensor aspect ratio matches image aspect ratio
  // Calculate the camera orientation
  const res = CalcCameraOrientation(inputPoints, focalLength, sensorWidth, sensorHeight, pixelWidth, pixelHeight);

  return res;
}

/**
 * Checks to see if the angle of attack of the photo is valid.
 * @param {CameraOrientation?} cameraOrientation - The camera orientation data.
 * @returns {PhotoWarning?} A warning object if the angle of attack is invalid, otherwise null.
 */
function checkAnglesOfAttack(cameraOrientation) {
  if (!cameraOrientation) return null;
  const { lowerAngleOfAttack, upperAngleOfAttack } = cameraOrientation;
  const angleLimit = Convert(51, 'deg', 'rad');
  const angleOfAttackIsValid = lowerAngleOfAttack >= angleLimit && upperAngleOfAttack >= angleLimit;

  // We need to decide on a threshold for how far from orthogonal the measurements can be
  return !angleOfAttackIsValid
    ? {
        severity: WARNING_SEVERITY_TYPES.WARNING,
        category: 'invalid_angle_of_attack',
        description: `The subject of the photo is closer than recommended. Close proximity can magnify the effect of errors in stick placement, calibration, leaning or warped poles, parallax, and lens distortion.`,
        type: 'photo'
      }
    : null;
}

/**
 * @param {CameraOrientation?} cameraOrientation
 * @param {number} highestMarkerHeight The height of the highest marker in the photo.
 * @returns {PhotoWarning?} A warning object if the distance is invalid, otherwise null.
 */
function checkDistance(cameraOrientation, highestMarkerHeight) {
  if (!cameraOrientation) return null;
  if (!highestMarkerHeight) return null;
  const { distToStickBottom } = cameraOrientation;
  const distanceLimit = highestMarkerHeight * 1.1;
  const distanceIsValid = distToStickBottom >= distanceLimit;

  return !distanceIsValid
    ? {
        severity: WARNING_SEVERITY_TYPES.WARNING,
        category: 'invalid_distance',
        description: `The subject of the photo is closer than recommended. Close proximity can magnify the effect of errors in stick placement, calibration, leaning or warped poles, parallax, and lens distortion.`,
        type: 'photo'
      }
    : null;
}

/**
 * @param {object} photoData - The photo data (from `photos.<photo-id>` on the job).
 * @returns {PhotoWarning?} A warning object if the photo was uploaded via the Cloneable integration, otherwise null.
 */
function checkIsCloneablePhoto(photoData) {
  const isCloneablePhoto = photoData?.cloneable_photo;
  return isCloneablePhoto
    ? {
        severity: WARNING_SEVERITY_TYPES.INFO,
        category: 'cloneable_photo',
        description: 'Calibrated heights are provided by Cloneable.ai.',
        type: 'photo'
      }
    : null;
}

/**
 * @param {object} photoData - The photo data (from `photos.<photo-id>` on the job).
 * @returns {PhotoWarning?} A warning object if the photo was uploaded via the Azmyth integration, otherwise null.
 */
function checkIsAzmythPhoto(photoData) {
  const isAzmythPhoto = photoData?.azmyth_photo;
  return isAzmythPhoto
    ? {
        severity: WARNING_SEVERITY_TYPES.INFO,
        category: 'azmyth_photo',
        description: 'Calibrated heights are provided by Azmyth.',
        type: 'photo'
      }
    : null;
}

/**
 * Determines if a marker is off the photo and returns true or false if it does
 * @param {object} pixelSelection - The pixel selection data for the marker.
 */
export function markerIsOffPhotoHelper(pixelSelection) {
  const { percentX, percentY } = pixelSelection;
  if (!percentX || !percentY) return false;
  return percentX < 0 || percentX > 100 || percentY < 0 || percentY > 100;
}

/**
 * Returns the color to use for the anchor marker based on its scores
 * @param {Object} anchorMarkerData - The anchor marker data
 * @param {String} [uncalibratedColor] - The default color to use if the anchor is not calibrated (typically comes from the model)
 */
export function getAnchorColor(anchorMarkerData, uncalibratedColor) {
  uncalibratedColor ??= 'rgba(100,100,255,0.8)';
  const criteria = calibratePhoto.criteria;
  const { _score: alignmentScore, _residual_score: residualScore } = anchorMarkerData;
  if (alignmentScore == null) return uncalibratedColor;
  if (alignmentScore <= criteria.anchorFlagYwRdReq || residualScore <= criteria.anchorFlagYwRdReq) {
    return '#FF004D';
  } else if (alignmentScore <= criteria.anchorFlagGnYwReq || residualScore <= criteria.anchorFlagGnYwReq) {
    return '#FFFF00';
  } else if (anchorMarkerData?._measured_height != null) {
    return uncalibratedColor;
  } else {
    return '#74FF00';
  }
}

/**
 * Determines if a marker is too close to the top or bottom edge of a photo and returns a warning if it is.
 * @param {object} markerData - The data for the marker.
 * @param {object} supplementalData - Data for generating the marker warning.
 * @param {string} supplementalData.markerPath - The dot-delimited path to the marker.
 * @param {object} supplementalData.photoData - The data for the photo the marker is on.
 * @returns {PhotoWarning?} A warning object if the marker is too close to the edge, otherwise undefined.
 */
function getMarkerIsTooCloseToEdge(markerData, supplementalData) {
  // Get proper data from parameters
  const pixelSelection = markerData?.pixel_selection?.[0];
  const { markerPath, photoData } = supplementalData;

  // Get the height of the photo and the percent y of the marker
  const photoHeight = photoData?.image_height;
  const percentY = pixelSelection?.percentY;
  if (photoHeight == null || percentY == null) return null;

  // Check to see if the marker is off the photo.  If it is, don't check the warning
  const markerIsOffPhoto = markerIsOffPhotoHelper(pixelSelection);
  if (markerIsOffPhoto) return null;

  // Get the x and y coordinates using the pixel selection
  const yCoord = (percentY / 100) * photoHeight;

  // A marker is too close to the edge if it is within 7% of the top or bottom edge of the photo
  const edgeDistanceInPixels = photoHeight * 0.07;
  const maxYDistance = photoHeight - edgeDistanceInPixels;
  const tooCloseToTopEdge = yCoord > maxYDistance;
  const tooCloseToBottomEdge = yCoord < edgeDistanceInPixels;

  // Make the warning config
  const tooCloseToEdgeWarning = {
    severity: WARNING_SEVERITY_TYPES.INFO,
    category: 'too_close_to_edge',
    description: `Marker is close to the vertical edge of the photo. Framing the subject of the photo with space above and below will reduce the effects of distortion around the photo's edge.`,
    type: 'marker',
    markerPath
  };

  // show the warning if the marker is too close to the edge.  Otherwise, return null
  return tooCloseToTopEdge || tooCloseToBottomEdge ? tooCloseToEdgeWarning : null;
}

/**
 * Determines if a marker is too far from the stick align and returns a warning if it is.
 *
 * @param {Object} markerData - The data related to the marker.
 * @param {Object} supplementalData - Additional data required for the calculation.
 * @param {Object} supplementalData.markerPath - The path to the marker.
 * @param {Object} supplementalData.photoData - The data related to the photo.
 * @param {Object} supplementalData.stickAlignParameters - The data related to the stick align.
 * @returns {PhotoWarning?} - Returns a warning object if the marker is too far from the stick align, otherwise returns null.
 */
function getMarkerIsTooFarFromStickAlign(markerData, supplementalData) {
  // Get proper data from parameters
  const pixelSelection = markerData?.pixel_selection?.[0];
  const { markerPath, photoData, stickAlignParameters } = supplementalData;

  // Get the height of the photo and the percent x of the marker
  const photoWidth = photoData?.image_width;
  const photoHeight = photoData?.image_height;
  const { percentX, percentY } = pixelSelection ?? {};
  if (photoWidth == null || photoHeight == null || percentX == null || percentY == null) return null;

  // Check to see if the marker is off the photo.  If it is, don't check the warning
  const markerIsOffPhoto = markerIsOffPhotoHelper(pixelSelection);
  if (markerIsOffPhoto) return null;

  // Get the y coordinate using the pixel selection
  const yCoord = (percentY / 100) * photoHeight;

  // A marker is too far from the stick align if it is not within the box centered on the stick align that has a width of 33% of the photo width
  const { slope, intercept } = stickAlignParameters ?? {};
  if (slope == null || intercept == null) return null;

  // Get the coordinate of the point on the stick align line that has the same y coordinate as the marker
  const stickAlignX = (yCoord - intercept) / slope;
  const stickAlignPercentX = (stickAlignX / photoWidth) * 100;
  const markerDistanceFromStickAlign = Math.abs(percentX - stickAlignPercentX);
  const markerIsTooFarFromStickAlign = markerDistanceFromStickAlign > 33 / 2;

  // Make the warning config
  const markerTooFarFromStickAlignWarning = {
    severity: WARNING_SEVERITY_TYPES.INFO,
    category: 'outside_expected_measurement_area',
    description: `Marker is outside the expected measurement area. Measurement accuracy may be degraded.`,
    type: 'marker',
    markerPath
  };

  // show the warning if the marker is too far from the stick align.  Otherwise, return null
  return markerIsTooFarFromStickAlign ? markerTooFarFromStickAlignWarning : null;
}

/**
 * Checks if a marker is above the pole top marker.
 *
 * @param {Object} markerData - The data related to the marker.
 * @param {Object} supplementalData - Additional data required for the calculation.
 * @param {Object} supplementalData.markerPath - The path to the marker.
 * @param {Object} supplementalData.photoData - The data related to the photo.
 * @returns {PhotoWarning?} - Returns a warning object if the marker is too close to the edges, otherwise returns null.
 */
function checkMarkerAbovePoleTop(markerData, supplementalData) {
  const { markerPath, photoData } = supplementalData;

  // We already have a warning for multiple pole top markers, so only add the warning for non-pole top markers
  const markerType = markerPath.split('.')[0];
  const markerIsPoleTopMarker = markerType == 'pole_top';
  if (markerIsPoleTopMarker === true) return null;

  // Get the pole top markers and find the height of the lowest one
  let poleTopMarkers = photoData?.photofirst_data?.pole_top ?? {};
  poleTopMarkers = Object.values(poleTopMarkers ?? {}).sort(
    (a, b) => (a._measured_height ?? a.manual_height) - (b._measured_height ?? b.manual_height)
  );
  const lowestPoleTopMarker = poleTopMarkers[0];
  const lowestPoleTopMarkerHeight = lowestPoleTopMarker?._measured_height ?? lowestPoleTopMarker?.manual_height;

  // Check if the marker is above the lowest pole top marker
  const markerHeight = markerData?._measured_height ?? markerData?.manual_height;
  const markerAbovePoleTop = markerHeight > lowestPoleTopMarkerHeight;

  // Make the warning config
  const markerAbovePoleTopWarning = {
    severity: WARNING_SEVERITY_TYPES.INFO,
    category: 'marker_above_pole_top',
    description: `Marker is above the pole top.`,
    type: 'marker',
    markerPath
  };

  return markerAbovePoleTop ? markerAbovePoleTopWarning : null;
}

/**
 * Checks for warnings related to pole top markers.
 *
 * @param {Object} photoFirstData - The photo data object containing pole top marker information.
 * @param {Object} [photoFirstData.pole_top] - An object representing pole top markers.
 * @returns {PhotoWarning[]} An array of warning objects.
 */
function checkPoleTopMarkers(photoFirstData) {
  const poleTopMarkers = Object.values(photoFirstData?.pole_top || {});
  const warnings = [];
  // Check for multiple pole top markers
  if (poleTopMarkers.length > 1) {
    warnings.push({
      severity: WARNING_SEVERITY_TYPES.ERROR,
      category: 'multiple_pole_top_markers',
      description: `Multiple pole top markers found on pole.`,
      type: 'photo'
    });
  }
  // Check for pole top extensions
  for (const poleTopMarker of poleTopMarkers) {
    if (poleTopMarker?.pole_top_extension == true) {
      const pteDocLink =
        'https://guide.katapultengineering.com/integrated-pole-loading-manual#:~:text=some%20additional%20steps%3A-,Pole%20top%20extensions,-.%20Place%20the%20pole';
      warnings.push({
        severity: WARNING_SEVERITY_TYPES.WARNING,
        category: 'pole_top_extension_marker',
        description: `Pole top marker is marked as an extension. Pole top extensions are not fully supported. See <a href="${pteDocLink}" target="_blank" rel="noopener noreferrer">this document</a> for more information on modeling pole top extensions.`,
        type: 'photo'
      });
      break;
    }
  }

  return warnings;
}

/**
 * Checks for high anchor residuals in the photo data. The idea is to detect if placed anchors deviate from a line
 * @param {Object} photoData
 * @param {number} photoData.image_width
 * @param {number} photoData.image_height
 * @param {number} photoData.rotation
 * @param {Object} photoData.photofirst_data
 * @param {Object} photoData.photofirst_data.anchor_calibration
 * @returns {Object|null} Warning object if high anchor residuals are detected, null otherwise.
 */
function checkAnchorResiduals(photoData) {
  const RESIDUAL_SCORE_THRESHOLD = 0.995; // This equates to a 20px residual on a 4000px wide image

  const anchorMarkers = photoData.photofirst_data?.anchor_calibration ?? {};

  const anchorCalibrationMarkerEntries = Object.entries(anchorMarkers);
  const residualScores = anchorCalibrationMarkerEntries
    .map(([, marker]) => marker?._residual_score)
    .filter((score) => typeof score === 'number')
    .sort();
  const minResidualScore = residualScores[0] ?? 0;

  if (minResidualScore <= RESIDUAL_SCORE_THRESHOLD) {
    return {
      severity: WARNING_SEVERITY_TYPES.WARNING,
      category: 'high_anchor_residuals',
      description: `The placed anchor markers deviate from the expected pattern. Make sure that the anchor markers are accurately placed on the calibration stick and the stick isn't bent or misaligned.`,
      type: 'photo'
    };
  }

  return null;
}

/**
 * Checks anchor alignment score for a marker.
 * @param {Object} markerData - The marker data.
 * @param {Object} supplementalData - Supplemental data for generating the warnings.
 */
function checkAnchorAlignmentScore(markerData, supplementalData) {
  const { _score: alignmentScore } = markerData;
  const { markerPath, photoData } = supplementalData;

  const markerPathParts = markerPath.split('.');
  const markerType = markerPathParts[0];
  if (markerType != 'anchor_calibration') return null;

  if (alignmentScore == null || alignmentScore > calibratePhoto.criteria.anchorFlagGnYwReq) return null;
  if (alignmentScore > calibratePhoto.criteria.anchorFlagYwRdReq)
    return {
      severity: WARNING_SEVERITY_TYPES.WARNING,
      category: 'anchor_alignment_score',
      description: `This anchor has a low alignment score.`,
      type: 'marker',
      markerPath
    };
  return {
    severity: WARNING_SEVERITY_TYPES.ERROR,
    category: 'anchor_alignment_score',
    description: `This anchor has a poor alignment score.`,
    type: 'marker',
    markerPath
  };
}
